import type { Goal, GoalCalc, GoalPace, GoalType } from '../../../hooks/useGoals';

interface GoalCardProps {
  goal: Goal;
  calc: GoalCalc;
  onView: () => void;
  onEdit: () => void;
  onAddContribution: () => void;
  onDelete: () => void;
}

const PACE_CONFIG: Record<GoalPace, { label: string; color: string; bg: string; icon: string }> = {
  completed: { label: 'Concluída', color: 'text-emerald-700', bg: 'bg-emerald-100', icon: 'ri-check-line' },
  on_track: { label: 'No ritmo', color: 'text-emerald-600', bg: 'bg-emerald-50', icon: 'ri-checkbox-circle-line' },
  at_risk: { label: 'Em risco', color: 'text-amber-600', bg: 'bg-amber-50', icon: 'ri-error-warning-line' },
  behind: { label: 'Atrasada', color: 'text-red-600', bg: 'bg-red-50', icon: 'ri-time-line' },
  overdue: { label: 'Vencida', color: 'text-red-700', bg: 'bg-red-100', icon: 'ri-alarm-warning-line' },
  no_date: { label: 'Sem prazo', color: 'text-gray-500', bg: 'bg-gray-100', icon: 'ri-calendar-line' },
};

const TYPE_LABELS: Record<GoalType, string> = {
  reserva_emergencia: 'Reserva',
  viagem: 'Viagem',
  quitar_divida: 'Dívida',
  investimento: 'Investimento',
  entrada_imovel: 'Imóvel',
  compra_pessoal: 'Compra',
  outro: 'Objetivo',
};

const PRIORITY_DOT: Record<string, string> = {
  high: 'bg-red-400',
  medium: 'bg-amber-400',
  low: 'bg-gray-300',
};

const fmt = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const fmtDate = (d: string) => {
  const dt = new Date(d + 'T12:00:00');
  const today = new Date();
  const diffMs = dt.getTime() - today.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return `Venceu há ${Math.abs(diffDays)}d`;
  if (diffDays <= 30) return `${diffDays}d restantes`;
  if (diffDays <= 365) return `${Math.round(diffDays / 30)} meses`;
  return dt.toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' });
};

export const GoalCard = ({ goal, calc, onView, onEdit, onAddContribution, onDelete }: GoalCardProps) => {
  const pace = PACE_CONFIG[calc.pace];
  const typeLabel = TYPE_LABELS[goal.type] ?? 'Objetivo';

  return (
    <div
      className="bg-white rounded-2xl border border-gray-100 hover:border-gray-200 transition-all group cursor-pointer overflow-hidden"
      onClick={onView}
    >
      {/* Color accent top bar */}
      <div className="h-1 w-full" style={{ backgroundColor: goal.color }} />

      <div className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3 min-w-0">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: goal.color + '20' }}
            >
              <i className={`${goal.icon || 'ri-trophy-line'} text-lg`} style={{ color: goal.color }} />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 mb-0.5">
                <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${PRIORITY_DOT[goal.priority] ?? 'bg-gray-300'}`} />
                <p className="text-sm font-700 text-gray-900 truncate">{goal.name}</p>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-md font-500">{typeLabel}</span>
                {goal.target_date && (
                  <span className="text-xs text-gray-400">· {fmtDate(goal.target_date)}</span>
                )}
              </div>
            </div>
          </div>

          {/* Actions (hover) */}
          <div
            className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 ml-2"
            onClick={e => e.stopPropagation()}
          >
            <button
              onClick={onAddContribution}
              className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-emerald-50 text-gray-300 hover:text-emerald-600 cursor-pointer"
              title="Registrar aporte"
            >
              <i className="ri-add-circle-line text-sm" />
            </button>
            <button
              onClick={onEdit}
              className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-300 hover:text-gray-600 cursor-pointer"
            >
              <i className="ri-edit-line text-sm" />
            </button>
            <button
              onClick={onDelete}
              className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 text-gray-300 hover:text-red-500 cursor-pointer"
            >
              <i className="ri-delete-bin-line text-sm" />
            </button>
          </div>
        </div>

        {/* Amounts row */}
        <div className="grid grid-cols-3 gap-2 mb-3 text-center">
          <div>
            <p className="text-xs text-gray-400 mb-0.5">Acumulado</p>
            <p className="text-xs font-700 text-gray-800">{fmt(goal.current_amount)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400 mb-0.5">Meta</p>
            <p className="text-xs font-700 text-gray-800">{fmt(goal.target_amount)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400 mb-0.5">Faltam</p>
            <p className="text-xs font-700 text-red-500">{calc.remaining > 0 ? fmt(calc.remaining) : '—'}</p>
          </div>
        </div>

        {/* Progress bar */}
        <div className="mb-3">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-gray-400">Progresso</span>
            <span className="text-xs font-700" style={{ color: goal.color }}>{Math.round(calc.progress)}%</span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{ width: `${calc.progress}%`, backgroundColor: goal.color }}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between">
          {calc.monthlyNeeded && calc.monthlyNeeded > 0 ? (
            <p className="text-xs text-gray-400">
              <span className="font-600 text-gray-700">{fmt(calc.monthlyNeeded)}/mês</span> sugerido
            </p>
          ) : calc.remaining === 0 ? (
            <p className="text-xs text-emerald-600 font-600 flex items-center gap-1">
              <i className="ri-check-line" />Meta atingida!
            </p>
          ) : (
            <p className="text-xs text-gray-400">Sem data definida</p>
          )}

          <span className={`flex items-center gap-1 text-xs font-600 px-2 py-1 rounded-lg ${pace.bg} ${pace.color}`}>
            <i className={`${pace.icon} text-xs`} />
            {pace.label}
          </span>
        </div>
      </div>
    </div>
  );
};
